import { DOCUMENT, effect, inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { distinctUntilChanged, map, Observable, startWith } from 'rxjs';

type AppTheme = 'light' | 'dark';

type MonacoGlobal = {
  editor?: {
    setTheme(name: string): void;
    onDidCreateEditor?(cb: (editor: unknown) => void): { dispose(): void };
  };
};

/** The attribute @ol/kit's OlThemeService writes on <html> (absent = light). */
const THEME_ATTR = 'data-ol-theme';

/** Maps the app theme to Monaco's built-in themes. */
export const monacoThemeFor = (theme: AppTheme): string => (theme === 'dark' ? 'vs-dark' : 'vs');

/**
 * The theme actually applied to the document, whoever applied it.
 *
 * Deliberately NOT `ThemeService.current`: standalone, the kit's OlThemeService
 * only knows about its own set()/toggle(); embedded, it mirrors the shell via
 * provideRemoteThemeSync() (AppEmbedModule). Watching the attribute itself
 * covers both builds with one code path and stays correct whoever writes the
 * shared `<html data-ol-theme>`.
 */
const documentTheme$ = (doc: Document): Observable<AppTheme> => {
  const root = doc.documentElement;
  return new Observable<void>((subscriber) => {
    const observer = new MutationObserver(() => subscriber.next());
    observer.observe(root, { attributes: true, attributeFilter: [THEME_ATTR] });
    return () => observer.disconnect();
  }).pipe(
    startWith(undefined),
    map((): AppTheme => (root.getAttribute(THEME_ATTR) === 'dark' ? 'dark' : 'light')),
    distinctUntilChanged(),
  );
};

/**
 * Keeps Monaco's (single, global) editor theme in sync with the @ol/kit dark
 * theme (OL-11878).
 *
 * Monaco has exactly ONE theme for every editor on the page, and several hub
 * components pass their own `theme` in editor options ('vs', 'vs-light',
 * 'vs-dark'), each of which silently restyles all other editors on create.
 * Light mode keeps that pre-existing behavior untouched (apps that do nothing
 * stay pixel-identical). Dark mode ENFORCES 'vs-dark':
 * - `onMonacoLoad` (wired in `provideMonacoEditorConfig`) applies it once the
 *   lazily-loaded Monaco is available and registers the create hook;
 * - `onDidCreateEditor` re-asserts it after any editor whose options carry a
 *   light theme;
 * - the effect re-applies on every theme change while editors are open —
 *   including one made by an embedding shell (see `documentTheme$`).
 */
@Injectable({ providedIn: 'root' })
export class MonacoThemeSync {
  private readonly theme = toSignal(documentTheme$(inject(DOCUMENT)), { requireSync: true });
  private initialized = false;

  private readonly syncOnChange = effect(() => {
    const theme = this.theme();
    if (this.initialized) {
      this.setMonacoTheme(theme);
    } else {
      // First run at service creation: never restyle a light boot; monaco is
      // usually not even loaded yet.
      this.initialized = true;
      if (theme === 'dark') {
        this.setMonacoTheme(theme);
      }
    }
  });

  /** Called from `onMonacoLoad` — Monaco is guaranteed present on window. */
  onMonacoLoad(): void {
    const editor = this.monaco()?.editor;
    if (!editor) {
      return;
    }
    if (this.theme() === 'dark') {
      editor.setTheme(monacoThemeFor('dark'));
    }
    // Per-editor `theme` options apply globally on create — re-assert dark
    // afterwards (microtask: create applies its options synchronously).
    editor.onDidCreateEditor?.(() => {
      if (this.theme() === 'dark') {
        queueMicrotask(() => editor.setTheme(monacoThemeFor('dark')));
      }
    });
  }

  private setMonacoTheme(theme: AppTheme): void {
    this.monaco()?.editor?.setTheme(monacoThemeFor(theme));
  }

  private monaco(): MonacoGlobal | undefined {
    return (window as unknown as { monaco?: MonacoGlobal }).monaco;
  }
}
